import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Cloud,
  TrendingUp,
  BarChart3,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Clock,
  Database,
  GitBranch,
  Map,
} from 'lucide-react';
import { Tooltip } from '../ui/Tooltip';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { CaladanLogo } from './CaladanLogo';

type SourceHealth = 'up' | 'down' | 'degraded' | 'unknown';

interface SourceStatus {
  name: string;
  status: SourceHealth;
}

function useSourceHealth() {
  const [health, setHealth] = useState<SourceHealth>('unknown');

  useEffect(() => {
    let cancelled = false;

    async function check() {
      try {
        const res = await fetch('/api/status/sources', { credentials: 'include' });
        if (!res.ok) { setHealth('unknown'); return; }
        const json = await res.json() as { data: SourceStatus[] };
        const sources = json.data;
        const critical = sources.filter(s => s.name === 'EIA API' || s.name === 'PostgreSQL');
        if (critical.some(s => s.status === 'down')) { if (!cancelled) setHealth('down'); return; }
        if (sources.some(s => s.status === 'down' || s.status === 'degraded')) { if (!cancelled) setHealth('degraded'); return; }
        if (sources.every(s => s.status === 'up')) { if (!cancelled) setHealth('up'); return; }
        if (!cancelled) setHealth('unknown');
      } catch {
        if (!cancelled) setHealth('unknown');
      }
    }

    void check();
    const id = setInterval(() => void check(), 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return health;
}

const HEALTH_COLOR: Record<SourceHealth, string> = {
  up: 'var(--accent-success)',
  degraded: '#f59e0b',
  down: 'var(--accent-danger)',
  unknown: 'var(--text-tertiary)',
};

const HEALTH_LABEL: Record<SourceHealth, string> = {
  up: 'All sources up',
  degraded: 'Some sources degraded',
  down: 'Critical source down',
  unknown: 'Source status unknown',
};

interface NavItemDef {
  to: string;
  label: string;
  Icon: React.ElementType;
  group?: string;
}

const NAV_ITEMS: NavItemDef[] = [
  { to: '/weather',      label: 'Weather',       Icon: Cloud,       group: 'ANALYSIS' },
  { to: '/fundamentals', label: 'Fundamentals',  Icon: BarChart3,   group: 'ANALYSIS' },
  { to: '/flow-map',     label: 'Flow Map',      Icon: Map,         group: 'ANALYSIS' },
  { to: '/pricing',      label: 'Pricing',       Icon: TrendingUp,  group: 'ANALYSIS' },
  { to: '/spreads',      label: 'Spreads',       Icon: GitBranch,   group: 'ANALYSIS' },
  { to: '/models',       label: 'Models',        Icon: Cpu,         group: 'MODELS' },
  { to: '/cron',         label: 'Cron Jobs',     Icon: Clock,          group: 'SYSTEM' },
  { to: '/db-explorer',  label: 'DB Explorer',   Icon: Database,       group: 'SYSTEM' },
  { to: '/health',       label: 'Health',        Icon: AlertTriangle,  group: 'SYSTEM' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  forceShow?: boolean;
}

export function Sidebar({ collapsed, onToggle, forceShow }: SidebarProps) {
  const { sidebarHidden } = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const health = useSourceHealth();

  if (sidebarHidden && !forceShow) return null;

  const groups = Array.from(new Set(NAV_ITEMS.map(i => i.group)));

  return (
    <aside
      style={{
        width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)',
        transition: 'width var(--duration-slow) var(--ease-in-out)',
      }}
      className={[
        'flex flex-col shrink-0 h-full overflow-hidden chrome-dark',
        'border-r border-[var(--border-subtle)]',
        'z-20',
      ].join(' ')}
    >
      {/* Logo / App title */}
      <div
        className={[
          'flex items-center h-[var(--topbar-height)] px-4 border-b border-[var(--border-subtle)] shrink-0',
          collapsed ? 'justify-center' : 'gap-2',
        ].join(' ')}
      >
        <CaladanLogo size={22} className="text-[var(--accent-primary)] shrink-0" />
        {!collapsed && (
          <span className="font-[600] text-[var(--text-primary)] text-[15px] leading-none tracking-[-0.01em] truncate">
            Caladan Capital
          </span>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3">
        {groups.map(group => (
          <div key={group} className="mb-4">
            {!collapsed && (
              <p
                className="px-4 pb-2 pt-1 uppercase text-[var(--text-tertiary)]"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '0.14em',
                }}
              >
                [ {group} ]
              </p>
            )}
            {NAV_ITEMS.filter(item => item.group === group).map(item => (
              <NavItem
                key={item.to}
                item={item}
                collapsed={collapsed}
                active={location.pathname === item.to}
              />
            ))}
          </div>
        ))}
      </nav>

      {/* Source health indicator */}
      <div className="shrink-0 px-2 pt-2 border-t border-[var(--border-subtle)]">
        <Tooltip content={HEALTH_LABEL[health]} placement="right">
          <button
            onClick={() => navigate('/status')}
            className={[
              'flex items-center gap-2.5 w-full rounded-[var(--radius)]',
              collapsed ? 'justify-center h-10 w-10 mx-auto' : 'px-3 py-2',
              'text-[var(--text-tertiary)] cursor-pointer',
              'hover:bg-[var(--bg-surface)] hover:text-[var(--text-secondary)]',
              'transition-colors duration-[150ms] ease-out',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-primary)]',
            ].join(' ')}
            aria-label={HEALTH_LABEL[health]}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: HEALTH_COLOR[health],
                flexShrink: 0,
                boxShadow: health === 'up' ? `0 0 6px ${HEALTH_COLOR[health]}` : undefined,
              }}
            />
            {!collapsed && (
              <span className="text-[12px] font-[400] truncate">{HEALTH_LABEL[health]}</span>
            )}
          </button>
        </Tooltip>
      </div>

      {/* Collapse toggle */}
      <div className="shrink-0 p-2">
        <Tooltip content={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <button
            onClick={onToggle}
            className={[
              'flex items-center justify-center w-full rounded-[var(--radius)]',
              'h-8 text-[var(--text-tertiary)] cursor-pointer',
              'hover:bg-[var(--bg-surface)] hover:text-[var(--text-secondary)]',
              'transition-colors duration-[150ms] ease-out',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-primary)]',
            ].join(' ')}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </Tooltip>
      </div>
    </aside>
  );
}

function NavItem({
  item,
  collapsed,
  active,
}: {
  item: NavItemDef;
  collapsed: boolean;
  active: boolean;
}) {
  const { Icon } = item;

  const content = (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        [
          'relative flex items-center gap-3 mx-2',
          collapsed ? 'px-0 justify-center h-10 w-10' : 'px-3 py-2.5',
          'transition-colors duration-[150ms] ease-out',
          'leading-none uppercase',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white',
          isActive
            ? 'bg-white text-black'
            : 'text-[var(--text-secondary)] hover:bg-[#1B1B1B] hover:text-[var(--text-primary)]',
        ]
          .filter(Boolean)
          .join(' ')
      }
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: '11px',
        letterSpacing: '0.1em',
        fontWeight: 600,
      }}
    >
      {({ isActive }) => (
        <>
          <Icon
            size={16}
            strokeWidth={2}
            className={`shrink-0 transition-colors duration-[150ms] ${
              isActive ? 'text-black' : 'text-[var(--text-tertiary)]'
            }`}
          />
          {!collapsed && (
            <span className="truncate font-[400]">{item.label}</span>
          )}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip content={item.label} placement="right">
        {content}
      </Tooltip>
    );
  }

  return content;
}
